import { useState, useEffect } from 'react';
import { 
  FaTachometerAlt, 
  FaGasPump, 
  FaUser,
  FaSignOutAlt,
  FaChevronLeft,
  FaChevronRight,
  FaHistory,
  FaBars,
  FaTimes
} from 'react-icons/fa';
import { MdPerson, MdLocalGasStation } from 'react-icons/md';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Sidebar.css';

const Sidebar = ({ activeTab, onTabChange, collapsed, onToggleCollapse }) => {
  const { user, logout, apiService, tokenReady } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [combustiveis, setCombustiveis] = useState([]);
  const [loadingCombustiveis, setLoadingCombustiveis] = useState(true);
  const isCompraFacil = window.location.hostname === 'abastecimento.comprafacil.pi.gov.br';

  // Verifica tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setMobileOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Carregar combustíveis
  useEffect(() => {
    if (tokenReady && apiService) {
      carregarCombustiveis();
    }
  }, [tokenReady, apiService]);

  async function carregarCombustiveis() {
    try {
      const response = await apiService.getCombustiveisFornecedor();
      
      if (response.sucesso && response.combustiveis) {
        setCombustiveis(response.combustiveis);
      } else {
        console.log('Erro ao carregar combustiveis:', response.mensagem);
      }
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoadingCombustiveis(false);
    }
  }

  // Menus
  const menuSections = [
    {
      title: "MENU",
      items: [
        {
          id: 'inicio',
          label: 'Dashboard',
          icon: <FaTachometerAlt />,
          description: 'Visão geral'
        },
        {
          id: 'historico',
          label: 'Histórico',
          icon: <FaHistory />,
          description: 'Registros anteriores'
        },
        {
          id: 'perfil',
          label: 'Perfil',
          icon: <MdPerson />,
          description: 'Minha conta'
        }
      ]
    }
  ];

  // Verifica se o item atual está ativo
  const isItemActive = (itemId) => {
    return activeTab === itemId;
  };

  const handleItemClick = (itemId) => {
    if (onTabChange) {
      onTabChange(itemId);
    }
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <>
      {/* Botão do menu mobile */}
      {isMobile && (
        <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
          {mobileOpen ? <FaTimes /> : <FaBars />}
        </button>
      )}

      {/* Overlay para mobile */}
      {isMobile && mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Botão toggle sidebar */}
        {!isMobile && (
          <button className="sidebar-toggle" onClick={onToggleCollapse}>
            {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        )}

        {/* Logo */}
        <div className="sidebar-logo">
          <div className="logo-icon-sidebar">
            <FaGasPump />
          </div>
          {(!collapsed || isMobile) && (
            <div className="logo-text">
              {isCompraFacil ? (
                <>
                  <h2>CompraFácil</h2>
                  <span>Posto</span>
                </>
              ) : (
                <>
                  <h2>GuardVisio</h2>
                  <span>Posto</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Menu */}
        <nav className="sidebar-nav">
          {menuSections.map((section, idx) => (
            <div key={idx} className="sidebar-section">
              {(!collapsed || isMobile) && <div className="section-title">{section.title}</div>}
              
              {section.items.map((item) => (
                <button
                  key={item.id}
                  className={`sidebar-item ${isItemActive(item.id) ? 'active' : ''}`}
                  onClick={() => handleItemClick(item.id)}
                  title={collapsed && !isMobile ? item.label : ''}
                >
                  <div className="item-icon">{item.icon}</div>
                  {(!collapsed || isMobile) && (
                    <div className="item-content">
                      <span className="item-label">{item.label}</span>
                      <span className="item-description">{item.description}</span>
                    </div>
                  )}
                  {isItemActive(item.id) && <div className="item-indicator" />}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* Tabela de Preços */}
        {(!collapsed || isMobile) && (
          <div className="sidebar-prices">
            <div className="prices-header">
              <MdLocalGasStation className="prices-icon" />
              <span className="prices-title">Tabela de Preços</span>
            </div>
            {loadingCombustiveis ? (
              <div className="prices-loading">
                <div className="prices-spinner"></div>
              </div>
            ) : combustiveis.length === 0 ? (
              <div className="prices-empty">
                <span>Sem dados</span>
              </div>
            ) : (
              <div className="prices-list">
                {combustiveis.map((comb) => (
                  <div key={comb.id} className="price-item">
                    <span className="price-fuel">{comb.descricao}</span>
                    <span className="price-value">R$ {comb.valor.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="sidebar-footer">
          {(!collapsed || isMobile) && (
            <div className="user-info">
              <div className="user-avatar">
                {user?.nome?.charAt(0) || 'U'}
              </div>
              <div className="user-details">
                <p className="user-name">{user?.nome || 'Usuário'}</p>
                <p className="user-role">Frentista</p>
              </div>
            </div>
          )}
          
          <button 
            className="logout-btn" 
            onClick={logout} 
            title={collapsed && !isMobile ? "Sair" : ""}
          >
            <FaSignOutAlt />
            {(!collapsed || isMobile) && <span>Sair</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;